import { ReportMedia } from "@shared/lib/types/reports";
import { formatData } from "@shared/lib/utils/formatData";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  buildMediaHistoryDeltas,
  type MediaHistoryDelta,
} from "./buildMediaHistoryDeltas";
import styles from "./PerformanceHistory.module.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface PerformanceHistoryProps {
  reportMedia: ReportMedia;
}

type MetricType = "views" | "likes" | "comments" | "shares";

const METRIC_COLORS: Record<MetricType, string> = {
  views: "#635bff",
  likes: "#e11d48",
  comments: "#a855f7",
  shares: "#f59e0b",
};

const getMetricDelta = (entry: MediaHistoryDelta, metric: MetricType) => {
  switch (metric) {
    case "views":
      return entry.deltaViews;
    case "likes":
      return entry.deltaLikes;
    case "comments":
      return entry.deltaComments;
    case "shares":
      return entry.deltaShares;
    default:
      return 0;
  }
};

export function PerformanceHistory({ reportMedia }: PerformanceHistoryProps) {
  const [activeMetric, setActiveMetric] = useState<MetricType>("views");

  const historyData = useMemo(
    () => buildMediaHistoryDeltas(reportMedia.mediaDataHistory),
    [reportMedia.mediaDataHistory]
  );

  const chartData = useMemo(() => {
    return {
      labels: historyData.map((entry) => entry.formattedDate),
      datasets: [
        {
          label: `${activeMetric.charAt(0).toUpperCase()}${activeMetric.slice(1)} Delta`,
          data: historyData.map((entry) =>
            getMetricDelta(entry, activeMetric)
          ),
          backgroundColor: METRIC_COLORS[activeMetric],
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };
  }, [historyData, activeMetric]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "#1a1f36",
          padding: 8,
          titleFont: {
            size: 11,
            weight: "bold" as const,
          },
          bodyFont: {
            size: 11,
            weight: "bold" as const,
          },
          displayColors: false,
          callbacks: {
            label: (context: TooltipItem<"bar">) => {
              const value = context.parsed.y;
              if (value === null) return "";
              return `+${formatData(value)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 10,
            },
            color: "#697386",
            maxRotation: 45,
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "#f1f5f9",
          },
          ticks: {
            font: {
              size: 10,
            },
            color: "#697386",
            callback: (value: number | string) => {
              if (typeof value === "number") {
                return formatData(value);
              }
              return value;
            },
          },
        },
      },
    }),
    []
  );

  if (historyData.length === 0) {
    return null;
  }

  const chartWidth = Math.max(320, historyData.length * 56);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h4 className={styles.title}>Performance History (Delta)</h4>
        <div className={styles.metricSelector}>
          {(["views", "likes", "comments", "shares"] as MetricType[]).map(
            (metric) => (
              <button
                key={metric}
                type="button"
                onClick={() => setActiveMetric(metric)}
                className={`${styles.metricButton} ${
                  activeMetric === metric ? styles.active : ""
                }`}
              >
                {metric}
              </button>
            )
          )}
        </div>
      </div>

      <div className={styles.chartWrapper}>
        <div
          className={styles.chartContainer}
          style={{ width: `${chartWidth}px` }}
        >
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
}
